import { CredentialsManagerGridView } from '../view/CredentialsManagerGridView';
import { templateGzCredentialsManagerGrid } from '../view/template';
import { CredentialsManagerGridRepository } from '../repository/CredentialsManagerGridRepository';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../../../framework/BaseWebComponentController';
import { CredentialsManagerGridWebComponent } from '..';
import {
  AUTH_VALUES,
  CredentialGridData,
  CREDENTIALS,
  CredentialsManagerCreateRequest,
  CredentialsManagerEvents,
  CredentialsManagerGridSchema,
  MAX_FILE_SIZE,
  UploadFileResponse,
} from '../types';
import { GridEvents } from '../../../../../grid/types/common';
import { toggleVisibility } from '../../../../../utils/htmlElement.utils';
import { DropdownEvents } from '../../../../../ui/gzDropdown/types';
import { GzInputFileWebComponent } from '../../../../../ui/gzInputFile';
import { InputEvents } from '../../../../../ui/gzInput/types';
import { translateFn } from '../../../../../utils/translator.helper';
import { ButtonEvents } from '../../../../../ui/gzButton/types';
import { showToast } from '@nirvana/shared/common';
import { ToastSeverity } from '../../../../../ui/gzToastNotification/type';

export class CredentialsManagerGridController extends BaseWebComponentController {
  public component: CredentialsManagerGridWebComponent;
  public dataRepository: CredentialsManagerGridRepository;
  public view: CredentialsManagerGridView;
  public viewConstructor: typeof CredentialsManagerGridView;
  public dataRepositoryConstructor: typeof CredentialsManagerGridRepository;

  public selectedItems: Array<string> = [];
  private isPem = false;

  constructor(component: CredentialsManagerGridWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: CredentialsManagerGridView,
      viewTemplate: templateGzCredentialsManagerGrid,
      dataRepositoryConstructor: CredentialsManagerGridRepository,
    });
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$addCredentialsButton,
        event: ButtonEvents.CLICK,
        callback: this.addCredentialsToGrid.bind(this),
      },
      {
        element: this.view.dom.$gridView,
        event: CredentialsManagerEvents.OnDeleteCredential,
        callback: this.deleteCredential.bind(this),
      },
      {
        element: this.view.dom.$gridView,
        event: GridEvents.SELECT_ROWS,
        callback: this.validateGrid.bind(this),
      },
      {
        element: this.view.dom.$authenticationTypeFormField,
        event: DropdownEvents.OPTION_SELECTED,
        callback: this.onAuthTypeChange,
      },
      {
        element: this.view.dom.$pemFilesField,
        event: InputEvents.CHANGE,
        callback: this.onPemFileChange,
      },
    ];
  }

  private onPemFileChange(event: WebComponentEvent): void {
    this.view.dom.$pemFilePathField.value = event.detail.sourceEvent;
    toggleVisibility(this.view.dom.$pemFilePathField, event.detail.sourceEvent !== null);
    this.view.addFormGroup.validate();
  }

  private onAuthTypeChange(event: WebComponentEvent): void {
    const input: GzInputFileWebComponent = this.view.dom.$fileInput;

    if (input) {
      input.setFormValue(null);
    }
    toggleVisibility(this.view.dom.$pemFilePathField, false);

    if (this.view.hasEc2Enabled) {
      switch (event.detail.newValue.value) {
        case AUTH_VALUES.PEM_FILE:
          this.isPem = true;
          toggleVisibility(this.view.dom.$pemFilesField, true);
          toggleVisibility(this.view.dom.$passwordFormField, false);
          toggleVisibility(this.view.dom.$sizeHelperWrapper, true);
          this.view.addRequiredValidator(this.view.dom.$pemFilesField);
          this.view.removeValidators(this.view.dom.$passwordFormField);
          this.view.dom.$passwordFormField.value = '';
          this.view.dom.$passwordFormField.resetField();
          break;
        case AUTH_VALUES.PASSWORD:
          this.isPem = false;
          toggleVisibility(this.view.dom.$pemFilesField, false);
          toggleVisibility(this.view.dom.$passwordFormField, true);
          toggleVisibility(this.view.dom.$sizeHelperWrapper, false);
          this.view.addRequiredValidator(this.view.dom.$passwordFormField);
          this.view.removeValidators(this.view.dom.$pemFilesField);
          break;
      }
    }
  }

  private validatePemFile(): File | null {
    const input: GzInputFileWebComponent = this.view.dom.$fileInput;

    if (!input) {
      return null;
    }

    const file: FileList | null = input.getFormValue();
    if (!file) {
      this.view.dom.$authenticationTypeFormField.setError(translateFn('Invalid file uploaded'));
      toggleVisibility(this.view.dom.$sizeHelperWrapper, false);
      return null;
    }

    const pemFile = file[0] as File;

    if (pemFile.size > MAX_FILE_SIZE) {
      this.view.dom.$authenticationTypeFormField.setError(
        translateFn('The PEM file is too large. The size limit is 50kb.')
      );
      toggleVisibility(this.view.dom.$sizeHelperWrapper, false);
      return null;
    }
    return pemFile;
  }

  private async validateGrid(ev: WebComponentEvent): Promise<void> {
    this.view.isGridValid = ev.detail.selectedItemsData.length > 0;
    this.selectedItems = ev.detail.selectionStoreData;
    this.view.dom.$gridFormFieldValidation.validate();
  }

  private async deleteCredential(ev: WebComponentEvent): Promise<void> {
    const selectedItem: CredentialsManagerGridSchema = ev.detail.rowData.item;

    this.view.setLoadingState();
    try {
      await this.dataRepository.removeRecords(selectedItem);
    } catch (e) {
      console.log(e);
    }

    await this.render();
    this.view.removeLoadingState();
  }

  private async addCredentialsToGrid(): Promise<void> {
    const request: CredentialsManagerCreateRequest = {
      username: this.view.dom.$userFormField.value as string,
      password: this.view.dom.$passwordFormField.value as string,
      description: this.view.dom.$descriptionFormField.value as string,
      type: CREDENTIALS.CREDENTIAL_TYPE_MACHINE,
      isPem: this.isPem,
    };
    this.view.setLoadingState();

    this.isPem ? await this.createPemRecords(request) : await this.createRecords(request);

    this.view.removeLoadingState();
  }

  private async createRecords(request: CredentialsManagerCreateRequest): Promise<void> {
    try {
      await this.dataRepository.createRecords(request);
      await this.renderGrid();
    } catch (e) {
      this.showErrorToast(e.err);
    }
  }

  private showErrorToast(error: Error) {
    let errorMessage = this.translator.getGenericErrorMessage();

    if (error['exceptionType'] === 'Exceptions\\CredentialsSetAlreadyExists') {
      errorMessage = this.dependencies.translateFn(
        'This credentials set already exists'
      );
    }

    showToast(errorMessage, ToastSeverity.ERROR);
  }

  private async createPemRecords(request: CredentialsManagerCreateRequest): Promise<void> {
    const pem = this.validatePemFile();
    if (pem === null) {
      return;
    }

    const response = await this.uploadPemFile(pem, request);

    if (!response.success) {
      this.view.dom.$authenticationTypeFormField.setError(translateFn(response.message));
      toggleVisibility(this.view.dom.$sizeHelperWrapper, false);
    } else {
      await this.renderGrid();
    }
  }

  private async uploadPemFile(file: File, request: CredentialsManagerCreateRequest): Promise<UploadFileResponse> {
    const form = new FormData();
    form.append('pem', file);
    form.append('username', request.username);
    form.append('password', request.password);
    form.append('description', request.description);
    form.append('auth_type', `${this.view.dom.$authenticationTypeFormField.value}`);
    form.append('isPem', `${request.isPem}`);

    return this.dataRepository.uploadFile(form);
  }

  private async renderGrid(): Promise<void> {
    this.clearInput();
    await this.render();
  }

  private clearInput(): void {
    this.view.dom.$userFormField.value = '';
    this.view.dom.$userFormField.resetField();
    this.view.dom.$passwordFormField.value = '';
    this.view.dom.$passwordFormField.resetField();
    this.view.dom.$descriptionFormField.Input.setFormValue('');
    this.view.dom.$pemFilesField.Input.setFormValue(null);
    this.view.dom.$authenticationTypeFormField.Input.deselectTextString();
    this.view.dom.$authenticationTypeFormField.Input.selectedOption = null;
    this.view.dom.$authenticationTypeFormField.Input.clearInput();
    this.view.dom.$pemFilePathField.value = '';

    toggleVisibility(this.view.dom.$pemFilePathField, false);
    toggleVisibility(this.view.dom.$sizeHelperWrapper, false);
  }

  public async getDataForView(): Promise<CredentialGridData> {
    return {
      authTypes: this.dataRepository.getAuthTypes(),
    };
  }
}
